<template>
  <div class="flex flex-col h-full bg-neutral-50">
    <!-- Modern Header -->
    <div
      class="px-6 py-4 bg-white/95 backdrop-blur-sm sticky top-0 z-10"
      :style="{
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        borderBottom: '1px solid #e5e7eb'
      }"
    >
      <div class="flex items-center justify-between">
        <div class="flex items-center space-x-3">
          <button
            @click="goBack"
            class="cursor-pointer flex items-center justify-center w-8 h-8 rounded-lg text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 transition-all duration-200"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <div>
            <h2 class="text-xl font-semibold text-neutral-900">
              {{
                selectedCategory
                  ? formatCategoryName(selectedCategory)
                  : "All FAQs"
              }}
            </h2>
            <p class="text-sm text-neutral-500" v-if="!loading">
              {{ faqs.length }}
              {{ faqs.length === 1 ? "Artikel" : "Artikel" }} verfügbar
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- FAQ List with improved mobile scrolling -->
    <div class="flex-1 overflow-y-auto" style="min-height: 0;">
      <div class="px-6 py-4 pb-20">
        <!-- Loading state -->
        <div
          v-if="loading"
          class="flex flex-col items-center justify-center py-12"
        >
          <div class="relative">
            <div 
              class="w-12 h-12 rounded-full animate-spin"
              :style="{
                border: '4px solid #e5e7eb',
                borderTop: '4px solid #3b82f6'
              }"
            ></div>
          </div>
          <p class="text-neutral-500 text-sm mt-4">Artikel laden...</p>
        </div>

        <!-- No FAQs state -->
        <div
          v-else-if="faqs.length === 0"
          class="flex flex-col items-center justify-center py-16 text-center"
        >
          <div class="w-16 h-16 bg-neutral-100 rounded-2xl flex items-center justify-center mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-8 w-8 text-neutral-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1.5"
                d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-neutral-900 mb-2">
            Keine Artikel gefunden
          </h3>
          <p class="text-neutral-500 text-sm max-w-sm mb-6">
            {{
              selectedCategory
                ? `Es gibt keine Artikel in der Kategorie ${formatCategoryName(
                    selectedCategory
                  )}.`
                : "Derzeit sind keine Artikel verfügbar."
            }}
          </p>
          <button
            @click="goBack"
            class="cursor-pointer inline-flex items-center px-4 py-2 bg-neutral-800 text-white text-sm font-medium rounded-lg hover:bg-neutral-900 transition-colors duration-200"
            :style="{
              boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
            }"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Zurück zu Kategorien
          </button>
        </div>

        <!-- Modern FAQ Grid -->
        <div v-else class="space-y-3">
          <article
            v-for="(faq, index) in faqs"
            :key="faq.$id"
            class="group relative bg-white rounded-xl cursor-pointer overflow-hidden transition-all duration-200"
            :style="{
              border: '1px solid #d1d5db',
              animationDelay: `${index * 50}ms`
            }"
            @click="viewFAQ(faq)"
          >
            <!-- Hover overlay -->
            <div class="absolute inset-0 bg-gradient-to-r from-neutral-50/0 to-neutral-100/0 group-hover:from-neutral-50/30 group-hover:to-neutral-100/30 transition-all duration-300"></div>

            <div class="relative p-5">
              <div class="flex items-start justify-between">
                <div class="flex-1 min-w-0 pr-4">
                  <!-- Question Title -->
                  <h3 class="text-base font-semibold text-neutral-900 group-hover:text-neutral-800 transition-colors duration-200 leading-tight mb-2">
                    {{ faq.question }}
                  </h3>

                  <!-- Answer Preview -->
                  <p class="text-sm text-neutral-600 line-clamp-2 leading-relaxed">
                    {{ getPlainTextPreview(faq.answer) }}
                  </p>

                  <!-- Meta info with new author fields -->
                  <div class="flex items-center mt-3 space-x-4 text-xs text-neutral-500">
                    <!-- Author info with fallback to legacy fields -->
                    <span v-if="getAuthorName(faq)" class="flex items-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-3 w-3 mr-1"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                      {{ getAuthorName(faq) }}
                    </span>

                    <!-- Last updated in German -->
                    <span v-if="faq.last_updated" class="flex items-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-3 w-3 mr-1"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      {{ formatDate(faq.last_updated) }}
                    </span>
                  </div>
                </div>

                <!-- Modern Arrow -->
                <div class="flex items-center justify-center w-8 h-8 rounded-lg bg-neutral-50 group-hover:bg-neutral-200 transition-all duration-200 flex-shrink-0">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-4 w-4 text-neutral-400 group-hover:text-neutral-900 transition-colors duration-200"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </article>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { fetchFAQs, fetchFAQsByCategory } from "../../lib/appwriteFaq";

export default {
  name: "FAQListScreen",
  props: {
    selectedCategory: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      faqs: [],
      loading: true,
    };
  },
  watch: {
    // Watch for changes in the selectedCategory prop
    selectedCategory() {
      this.loadFAQs();
    },
  },
  async mounted() {
    await this.loadFAQs();
  },
  methods: {
    async loadFAQs() {
      this.loading = true;

      try {
        // Load FAQs based on selected category
        if (this.selectedCategory) {
          this.faqs = await fetchFAQsByCategory(this.selectedCategory);
        } else {
          this.faqs = await fetchFAQs();
        }
      } catch (error) {
        console.error("Error loading FAQs:", error);
        this.faqs = [];
      } finally {
        this.loading = false;
      }
    },

    goBack() {
      this.$emit("go-back");
    },

    viewFAQ(faq) {
      this.$emit("view-faq", faq);
    },

    // Get author name with fallback logic
    getAuthorName(faq) {
      if (!faq) return null;

      // Try new author field first, then fall back to legacy field
      return faq.authorName || faq.author || null;
    },

    formatCategoryName(category) {
      if (!category) return "General";

      // Convert snake_case or kebab-case to Title Case
      return category
        .replace(/[-_]/g, " ")
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },

    // Strip HTML tags and decode entities for preview
    getPlainTextPreview(html) {
      if (!html) return "";

      // Remove HTML tags
      const withoutTags = html.replace(/<[^>]*>/g, " ");

      // Decode HTML entities using a temporary textarea element
      const textarea = document.createElement("textarea");
      textarea.innerHTML = withoutTags;
      const decoded = textarea.value;

      // Clean up extra whitespace
      const cleaned = decoded.replace(/\s+/g, " ").trim();

      // Truncate if too long
      return cleaned.length > 120 ? cleaned.substring(0, 120) + "..." : cleaned;
    },

    // German date formatting
    formatDate(dateString) {
      if (!dateString) return "";

      try {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        if (diffDays < 1) {
          return "Heute";
        } else if (diffDays === 1) {
          return "Gestern";
        } else if (diffDays < 7) {
          return `vor ${diffDays} ${diffDays === 1 ? "Tag" : "Tagen"}`;
        } else if (diffDays < 30) {
          const weeks = Math.floor(diffDays / 7);
          return `vor ${weeks} ${weeks === 1 ? "Woche" : "Wochen"}`;
        } else if (diffDays < 365) {
          const months = Math.floor(diffDays / 30);
          return `vor ${months} ${months === 1 ? "Monat" : "Monaten"}`;
        } else {
          // For older dates, show the actual date in German format
          return date.toLocaleDateString("de-DE", {
            day: "numeric",
            month: "short",
            year:
              date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
          });
        }
      } catch (error) {
        return "";
      }
    },
  },
};
</script>

<style scoped>
/* Modern animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

article {
  animation: fadeInUp 0.3s ease-out forwards;
  opacity: 0;
}

/* Enhanced line clamp */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Smooth scrolling */
.overflow-y-auto {
  scroll-behavior: smooth;
}

/* Custom scrollbar */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #343536;
}

/* Backdrop blur support */
@supports (backdrop-filter: blur(12px)) {
  .bg-white\/95 {
    background-color: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(12px);
  }
}
</style>